import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Callout,
    DropdownMenu,
    Modal,
    PageHeader,
    SectionCard,
    Segmented,
    StatusTag,
    type StatusTone,
} from '@/components/ui';
import { useConsoleBreadcrumb } from '@/components/shell/ConsoleBreadcrumbContext';
import { useProvisioningSwitch } from './useProvisioningSwitch';
import { useRoleMapTable } from './RoleMap/useRoleMapTable';
import { useRoleCoverage } from './RoleCoverage/useRoleCoverage';
import { RoleMapBoardView } from './RoleMap/RoleMapBoardView';
import { RoleMapListView } from './RoleMap/RoleMapListView';
import { RoleMapAddModal } from './RoleMap/RoleMapAddModal';
import { RoleMapExportPopover } from './RoleMap/RoleMapExportPopover';
import { RoleMapMatrixView } from './RoleMap/RoleMapMatrixView';
import { RoleKeyCreateForm } from './RoleCoverage/RoleKeyCreateForm';
import type { MatrixCell } from './RoleCoverage/types';

/** The four renderings of one screen — see the header comment. */
type RoleMapView = 'board' | 'role' | 'matrix' | 'list';

const COVERAGE_VIEWS: RoleMapView[] = ['board', 'role', 'matrix'];

function parseView(raw: string | null): RoleMapView {
    if (raw === 'list' || raw === 'matrix' || raw === 'role') return raw;
    return 'board';
}

/**
 * EAA-1841/EAA-1817: `/console/role-map` — "Liên kết vai trò", the ONE screen
 * for role links.
 *
 * WHY THIS MERGED. `/console/role-coverage` and `/console/role-map` were built
 * by two tickets from two design frames and shipped as separate routes, but they
 * were never separate screens: the frames carry the same title ("Liên kết vai
 * trò"), the same rows, the same "Phạm vi" concept, and both write the same
 * `RoleMap` endpoints. `docs/design/handoff/UX-SPEC.md` says it outright —
 * "/console/role-map — the only view".
 *
 * FOUR VIEWS, AND WHY THAT IS THE RIGHT READING OF THE DESIGN. Frame 55's own
 * switcher has three items — "Theo ứng dụng · Theo vai trò · Ma trận" — and
 * frames 56–64 draw the flat list as a page of its own, one breadcrumb level
 * up. Because this route is merged (above), the list cannot be a separate page
 * here, so it joins the switcher as a fourth peer. That is a deliberate,
 * recorded divergence from the frames' page hierarchy, not an oversight: the
 * alternative is re-splitting the route, which is the thing EAA-1841 merged on
 * purpose.
 *
 * What each view is for — they are genuinely different questions:
 *   - **Theo ứng dụng** (frame 55) — "what is THIS app missing?" A rail of apps
 *     plus the selected app's standard-role table, with the chip editor inline.
 *     One app at a time by construction, because the editor edits one app.
 *   - **Theo vai trò** — the same board with the axes swapped: "where does THIS
 *     standard role land?" No frame of its own; it is frame 55 read the other
 *     way round, which is what its own switcher item promises.
 *   - **Ma trận** (frame 55a) — every role against every app at once. The only
 *     view where an absent link is visible as an empty cell rather than
 *     inferred from a count.
 *   - **Danh sách** (frames 56–59) — operate on the links that exist: sort,
 *     filter, select rows, bulk re-scope/re-sync/delete, export CSV.
 *
 * SCOPE DIFFERS BY VIEW, and that is a backend constraint. The three coverage
 * views read `GET /role-map/coverage`, which filters `organization__isnull=True`
 * and takes no organization param, so they are ecosystem-default only. The
 * list's "Phạm vi" control lives in its own toolbar and is unavailable in the
 * coverage views — the subtitle says which slice is on screen instead of
 * showing a dead control.
 *
 * QUERY GATING, precisely: the COVERAGE hook is gated on `isCoverage`, so the
 * three coverage views' four queries are idle while the list is on screen. The
 * LIST hook is not gated at all any more — frames 55/55a put its `Xuất CSV` and
 * `Thêm liên kết` in the title strip of every view, and a gated hook backs those
 * with an empty application list. Three of its four queries share query keys
 * with the coverage hook, so the overlap resolves from one cache entry.
 */
export function RoleMapPage() {
    const { t } = useTranslation();
    const provisioning = useProvisioningSwitch();
    const [searchParams, setSearchParams] = useSearchParams();

    const VIEW_OPTIONS = [
        { label: t('console.roleMap.page.viewByApp'), value: 'board' },
        { label: t('console.roleMap.page.viewByRole'), value: 'role' },
        { label: t('console.roleMap.page.viewMatrix'), value: 'matrix' },
        { label: t('console.roleMap.page.viewList'), value: 'list' },
    ];

    const view: RoleMapView = parseView(searchParams.get('view'));
    const isCoverage = COVERAGE_VIEWS.includes(view);

    /**
     * EAA-1817: the list hook is active on EVERY view, not just `list`.
     *
     * It used to be gated (`active: view === 'list'`) so a hidden view fetched
     * nothing. But frames 55 and 55a both draw `Xuất CSV` and `Thêm liên kết` in
     * the title strip, and both are driven from here — gated, the Add-link modal
     * mounted with an empty application list and a permanently disabled submit,
     * which is why they used to be hidden outside the list. The cost of
     * un-gating is small and mostly notional: three of this hook's four queries
     * (`role-maps`, `apps`, `role-keys`) use the same query keys the coverage
     * views already fetch, so they resolve from one cache entry either way.
     */
    const listVm = useRoleMapTable({ active: true });
    const boardVm = useRoleCoverage(isCoverage);

    const setView = useCallback(
        (next: RoleMapView) => {
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    params.set('view', next);
                    return params;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    /**
     * Clicking a matrix cell hands off to the per-app board, because that is
     * where the editor is: frame 55a has no editor panel of its own, and
     * inventing one would put two chip editors in the app with two chances to
     * drift. `selectCell` already sets both halves of the target (the app, and
     * a focus request for the role row) — the same call the deleted
     * `CoverageMatrixCard` made, so the board lands scrolled to the right row.
     */
    const handleOpenCell = useCallback(
        (cell: MatrixCell) => {
            boardVm.board.selectCell(cell);
            setView('board');
        },
        [boardVm.board, setView]
    );

    const deepLinkApp = searchParams.get('application');
    const { apps, toggleApp } = listVm;
    useEffect(() => {
        if (!deepLinkApp) return;
        if (!apps.some((app) => app.code === deepLinkApp)) return;
        toggleApp(deepLinkApp);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete('application');
                next.set('view', 'list');
                return next;
            },
            { replace: true }
        );
    }, [deepLinkApp, apps, toggleApp, setSearchParams]);

    useConsoleBreadcrumb([
        { label: t('console.roleMap.page.breadcrumbParent') },
        { label: t('console.roleMap.page.title') },
    ]);

    if (!listVm.canView) return null;

    const subtitle = isCoverage
        ? t('console.roleMap.page.subtitleBoard', {
              appCount: boardVm.appCount,
              roleKeyCount: boardVm.roleKeyCount,
          })
        : t('console.roleMap.page.subtitleList', {
              totalLinks: listVm.totalLinks,
              appCount: listVm.appCount,
          });

    /**
     * Frame 55/55a's four title-strip counts. They are whole-grid tallies from
     * the coverage response, so they only exist for the coverage views — the
     * list view has its own footer counts over a different population (rows in
     * the selected scope, which includes per-org overrides the coverage
     * endpoint never returns). Showing them there would put two disagreeing
     * numbers on one page.
     */
    const tallies = boardVm.matrix.tallies;
    /**
     * The three DEFECT pills are hidden at zero. "0 chưa phủ" is not
     * information, it is a slot reporting that it has nothing to report — and
     * four pills that never change shape make the one that does harder to
     * notice, which is the whole job of a red count. The coverage pill always
     * shows: `8 đã liên kết / 10` is the headline either way.
     */
    const pills: { key: string; label: string; tone: StatusTone }[] = [
        {
            key: 'linked',
            label: t('console.roleMap.page.pillLinked', {
                count: tallies.linkedCount,
                total: tallies.cellCount,
            }),
            tone: 'success',
        },
    ];
    if (tallies.uncoveredCount > 0) {
        pills.push({
            key: 'uncovered',
            label: t('console.roleMap.page.pillUncovered', { count: tallies.uncoveredCount }),
            tone: 'warning',
        });
    }
    if (tallies.pausedCount > 0) {
        pills.push({
            key: 'paused',
            label: t('console.roleMap.page.pillPaused', { count: tallies.pausedCount }),
            tone: 'error',
        });
    }
    if (boardVm.matrix.notReadyCount > 0) {
        pills.push({
            key: 'notReady',
            label: t('console.roleMap.page.pillNotReady', {
                count: boardVm.matrix.notReadyCount,
            }),
            tone: 'default',
        });
    }

    const titlePills = isCoverage ? (
        <>
            {pills.map((pill) => (
                <StatusTag
                    key={pill.key}
                    status={pill.key}
                    config={{ [pill.key]: { label: pill.label, tone: pill.tone } }}
                />
            ))}
        </>
    ) : undefined;

    return (
        <main className="portal-admin portal-console-page portal-console-page--fit">
            <PageHeader
                title={t('console.roleMap.page.title')}
                subtitle={subtitle}
                titleExtra={titlePills}
                actions={
                    <>
                        <Segmented
                            options={VIEW_OPTIONS}
                            value={view}
                            onChange={(next) => setView(parseView(String(next)))}
                            aria-label={t('console.roleMap.page.viewToggleAriaLabel')}
                        />

                        {/* Frames 55 · 55a · 62 all draw these two in the title
                            strip, so they are not list-only. "Đồng bộ danh mục"
                            IS list-only: on the coverage views the same action
                            lives on the editor card's own header, next to the
                            catalog age it refers to.

                            EAA-1817 (frame 55c): export opens a scope popover
                            rather than downloading on click. */}
                        <RoleMapExportPopover
                            listVm={listVm}
                            matrixVm={boardVm.matrix}
                            coverageLoaded={isCoverage}
                        />

                        {(
                            <Button
                                type="primary"
                                onClick={listVm.openAdd}
                                disabled={!listVm.canManageCurrentScope}
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="16"
                                        height="16"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        aria-hidden="true"
                                    >
                                        <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                                    </svg>
                                }
                            >
                                {t('console.roleMap.page.addLink')}
                            </Button>
                        )}

                        {/* EAA-1817: "Đồng bộ danh mục" moved out of the button row
                            and into the overflow menu. Frames 55 · 55a · 62 all draw
                            exactly TWO buttons beside the switcher; with a four-item
                            switcher and three buttons the strip ran past the title
                            and wrapped. Sync is also the least frequent of the three
                            and the one with a per-app equivalent on the editor card,
                            so it is the right one to demote — not the one the design
                            draws as primary.

                            EAA-1817: "Tạo vai trò chuẩn" was added as a SECOND item
                            here, reported from the running app — the only place that
                            form existed was `NoRoleKeysEmptyState`, gated on
                            `roleKeyCount === 0`, so once one standard role existed the
                            form became permanently unreachable. No design frame puts
                            this control in the header — frame 60 draws it only on the
                            empty state — so putting it in the overflow menu is a
                            deliberate local decision, not a frame being followed. */}
                        {(listVm.canSync || boardVm.canManageRoleKeys) && (
                            <DropdownMenu
                                items={[
                                    ...(listVm.canSync
                                        ? [
                                              {
                                                  key: 'sync',
                                                  label: listVm.syncMutation.isPending
                                                      ? t('console.roleMap.page.syncing')
                                                      : t('console.roleMap.page.syncCatalog'),
                                                  disabled: listVm.syncMutation.isPending,
                                                  onSelect: () => listVm.syncMutation.mutate(),
                                              },
                                          ]
                                        : []),
                                    ...(boardVm.canManageRoleKeys
                                        ? [
                                              {
                                                  key: 'create-role-key',
                                                  label: t('console.roleMap.page.createRoleKey'),
                                                  onSelect: () =>
                                                      boardVm.roleKeyCreate.setOpen(true),
                                              },
                                          ]
                                        : []),
                                ]}
                                triggerAriaLabel={t('console.roleMap.page.moreActionsAria')}
                            />
                        )}
                    </>
                }
            />

            {/* EAA-1817 (2026-08-25, user report: a mapped Level never reached ACH).
                Every link on this screen is inert while the master switch is off —
                the mappings resolve correctly and then nothing is pushed, because
                `provisioning.apply` refuses and the refusal is swallowed. Editing
                links in that state feels like configuring a live system, so the
                page says otherwise before the operator spends an hour on it.

                A notice, not a `degraded` badge: dark-by-default is a deliberate
                operational state, not a role-map fault. */}
            {provisioning.off && (
                <Callout tone="warning">
                    {t('console.roleMap.page.provisioningOff', {
                        count: provisioning.pendingCount,
                    })}
                </Callout>
            )}

            {/* EAA-1817: mounted HERE, beside the "Thêm liên kết" trigger in the
                title strip, not inside the list view. Frames 55 · 55a · 62 all
                draw that button, so it exists on every view — and while its
                modal lived in `RoleMapListView` the button silently did nothing
                on the three coverage views. Reported from the running app. */}
            <RoleMapAddModal vm={listVm} />

            {/* EAA-1817: mounted here, driven by `boardVm.roleKeyCreate.open` rather
                than a second piece of local state — that VM already closes itself
                and invalidates `role-keys`/`role-map-coverage` on a successful
                create, so a separate flag here would fall out of sync with it after
                a submit. Works on every view: `useRoleKeyCreateForm` only needs
                `apiBase`/`token` (from `useRuntimeConfig`/`useAuth`), neither of
                which is gated by the coverage queries' `active` flag — see
                `useRoleCoverageData`. */}
            <Modal
                className="rc-role-key-create-modal"
                open={boardVm.roleKeyCreate.open}
                onCancel={() => boardVm.roleKeyCreate.setOpen(false)}
                title={t('console.roleMap.page.createRoleKeyModalTitle')}
                footer={null}
            >
                <RoleKeyCreateForm form={boardVm.roleKeyCreate} />
            </Modal>

            <div className="fit-grow">
                <RoleMapViewport
                    view={view}
                    boardVm={boardVm}
                    listVm={listVm}
                    onOpenCell={handleOpenCell}
                />
            </div>
        </main>
    );
}

/**
 * The one body region, per view. A small component rather than a chain of
 * ternaries in the page's JSX — four branches of a conditional returning four
 * different subtrees is exactly what the house style rules out.
 */
function RoleMapViewport({
    view,
    boardVm,
    listVm,
    onOpenCell,
}: {
    view: RoleMapView;
    boardVm: ReturnType<typeof useRoleCoverage>;
    listVm: ReturnType<typeof useRoleMapTable>;
    onOpenCell: (cell: MatrixCell) => void;
}) {
    if (view === 'list') {
        return (
            <SectionCard>
                <RoleMapListView vm={listVm} />
            </SectionCard>
        );
    }

    if (view === 'matrix') {
        return (
            <RoleMapMatrixView
                vm={boardVm.matrix}
                canManage={boardVm.canManage}
                selectedAppCode={boardVm.board.selectedAppCode}
                onOpenCell={onOpenCell}
            />
        );
    }

    return <RoleMapBoardView vm={boardVm} axis={view === 'role' ? 'role' : 'app'} />;
}

export default RoleMapPage;
